<template>
  <div class="flex sm:flex-row mb-4 flex-col gap-2 sm:gap-0">
    <div>
      <Button class="bg-[var(--color-orange)] text-white rounded-full px-4 h-10 my-0 mx-4"
      @click.prevent="toggleAssessment" icon="pi pi-plus"
      :label="$t('UserAdministrationComponent.options.createAssessment')" unstyled />
    </div>
    <Popover ref="pop">
      <div class="flex flex-col gap-4">
        <ul class="list-none p-0 m-0 flex flex-col">
          <li v-for="schema in getSchemas" :key="schema"
            class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer
            rounded-border">
            <Button class="bg-white text-black hover:font-semibold"
            :class="{ 'cursor-not-allowed text-gray-600':
            !selectedUsers || selectedUsers?.length === 0 }"
            @click.prevent="openAssessmentPopup(schema.type, undefined)" unstyled
            :disabled="!selectedUsers || selectedUsers?.length === 0 ">
              {{ schema.representationProperties.assessmentName[lang] || '-' }}
            </Button>
          </li>
        </ul>
      </div>
    </Popover>
    <div>
      <Button class="bg-[var(--color-orange)] text-white  rounded-full px-4 h-10 my-0 mx-4"
      @click.prevent="deleteCheckedUsers" icon="pi pi-trash"
      :label="$t('UserAdministrationComponent.options.deleteMarked')" unstyled />
    </div>
    <div>
      <Button class="bg-[var(--color-orange)] text-white  rounded-full px-4 h-10 my-0 mx-4"
      @click.prevent="toggle" icon="pi pi-cloud-download"
      :label="$t('UserAdministrationComponent.options.downloadCSV')" unstyled />
    </div>
    <Popover ref="op">
      <div class="flex flex-col gap-4">
        <ul class="list-none p-0 m-0 flex flex-col">
          <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer
            rounded-border">
            <Button @click="downloadSelectedUsersCSV" class="bg-white text-black
            hover:font-semibold" :class="{ 'cursor-not-allowed text-gray-600':
            !selectedUsers || selectedUsers?.length === 0 }" unstyled
            :disabled="!selectedUsers || selectedUsers?.length === 0">
              {{ $t("UserAdministrationComponent.downloadCSV.downloadSelected") }}
            </Button>
          </li>
          <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer
            rounded-border">
            <Button @click="downloadAllUsersCSV" class="bg-white text-black hover:font-semibold"
            unstyled>
              {{ $t("UserAdministrationComponent.downloadCSV.downloadAll") }}
            </Button>
          </li>
        </ul>
      </div>
    </Popover>
    <user-administration-filter-component
      @changeUserTableLoadingState="changeUserTableLoadingState"/>
  </div>
  <div class="w-full px-5 mb-3">
    <IconField>
      <InputIcon class="pi pi-search" />
      <InputText v-model="searchbar"
        :placeholder="$t('UserAdministrationComponent.placeholder')"
                class="w-full"/>
    </IconField>
  </div>
  <div class="card mx-4 overflow-x-auto">
    <DataTable v-model:selection="selectedUsers" :value="filteredUsers" paginator :rows="10"
      dataKey="_id" >
      <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
      <Column field="name" :header="$t('UserAdministrationComponent.columnNames.name')" sortable
        style="min-width: 14rem">
        <template #body="{ data }">
          <div class="flex flex-row gap-2 cursor-pointer items-center"
            @click.prevent="openPopup(data)">
            <div class="rounded-full overflow-hidden w-12">
              <img :src="getProfilePicture(data.picture_profile?.small)"
                alt="Profile picture" class="photo" />
            </div>
            <p>{{ data.name }}</p>
          </div>
        </template>
      </Column>
      <Column field="email" :header="$t('UserAdministrationComponent.columnNames.emailAddress')"
        sortable style="min-width: 24rem">
        <template #body="{ data }">
          {{ data.email }}
        </template>
      </Column>
      <Column field="currentLevel"
        :header="$t('UserAdministrationComponent.columnNames.level')"
        sortable style="min-width: 14rem">
        <template #body="{ data }">
          {{ data.currentLevel }}
        </template>
      </Column>
      <Column field="date_lastActivity"
       :header="$t('UserAdministrationComponent.columns.lastActivity')"
       sortable style="min-width: 14rem">
        <template #body="{ data }">
          <span>{{ formatDate(data.date_lastActivity) }}</span>
          <div v-if="getUserTimeOnPlatformDisplay(data._id)
            && getUserTimeOnPlatformDisplay(data._id)
              !== $t('UserAdministrationComponent.noTimeData')"
               class="text-xs text-gray-500 mt-1">
            <i class="pi pi-clock text-xs mr-1"></i>
            {{ getUserTimeOnPlatformDisplay(data._id) }}
          </div>
        </template>
      </Column>
      <Column field="activityStatus"
       :header="$t('UserAdministrationComponent.columns.activity')"
        sortable style="min-width: 14rem">
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <span
                  class="inline-block w-2.5 h-2.5 rounded-full"
                  :class="getUserActiveStatus(data._id) === 'active'
                    ? 'bg-green-500'
                    : 'bg-gray-400'"
                ></span>
                <span class="text-sm font-medium capitalize">
                  {{ getUserActiveStatus(data._id) }}
                </span>
              </div>
              <div class="text-xs text-gray-500">
                {{ getUserActivityLevel(data._id) }}
              </div>
            </div>
            <Button
              icon="pi pi-clock"
              size="small"
              text
              @click="showUserActivityChart(data)"
              :loading="isLoadingUserActivity(data._id)"
              class="p-1"
              v-tooltip.top="$t('UserAdministrationComponent.viewTimeOnPlatform')"
            />
          </div>
        </template>
      </Column>
    </DataTable>
    <SuperUserProfileOverlayComponent v-if="showDialog" :visible="showDialog"
    @update:visible="showDialog = $event" :data="notification">
    </SuperUserProfileOverlayComponent>
    <CreateAssessmentCalendarOverlayComponent v-if="showAssessmentDialog"
    :visible="showAssessmentDialog" @update:visible="showAssessmentDialog = $event"
    :data="notification"></CreateAssessmentCalendarOverlayComponent>

    <!-- User Activity Chart Dialog -->
    <Dialog
      v-model:visible="showActivityChartDialog"
      :header="`${selectedUserForChart?.name || 'User'} - Activity Overview`"
      modal
      :style="{ width: '80vw', maxWidth: '1000px' }"
      :closable="true"
      :closeOnEscape="true"
    >
      <UserActivityChartComponent
        v-if="showActivityChartDialog && selectedUserForChart"
        :user-id="selectedUserForChart._id"
        :user-name="selectedUserForChart.name"
        initial-period-type="weekly"
      />
    </Dialog>
  </div>
  <ProvideFeedbackComponent></ProvideFeedbackComponent>
</template>
<script>
import defaultProfileImage from '@/assets/defaultProfileImage.js';
import ProvideFeedbackComponent from '@/components/global/ProvideFeedbackComponent.vue';
import {
  Button,
  Column,
  DataTable,
  IconField,
  InputIcon,
  InputText,
  Popover,
  Dialog,
} from 'primevue';
// eslint-disable-next-line import/no-cycle
import NetworkAdmin from '@/helpers/networking/NetworkAdmin';
import SuperUserProfileOverlayComponent from '../Shared/FrontendOverlays/SuperUserProfileOverlayComponent.vue';
import CreateAssessmentCalendarOverlayComponent from '../Shared/FrontendOverlays/CreateAssessmentCalendarOverlayComponent.vue';
import UserAdministrationFilterComponent from './UserAdministrationFilterComponent.vue';
import UserActivityChartComponent from './UserActivityChartComponent.vue';
import { getActivitySummary } from '@/helpers/timeKpiHelper';

export default {
  name: 'UserAdministrationComponent',
  components: {
    DataTable,
    Column,
    ProvideFeedbackComponent,
    Button,
    Popover,
    Dialog,
    SuperUserProfileOverlayComponent,
    CreateAssessmentCalendarOverlayComponent,
    UserAdministrationFilterComponent,
    IconField,
    InputIcon,
    InputText,
    UserActivityChartComponent,
  },
  data() {
    return {
      selectedUsers: [],
      deleteButtonText: this.$t('UserAdministrationComponent.popup.deleteButton'),
      cancelButtonText: this.$t('UserAdministrationComponent.popup.cancelButton'),
      showDialog: false,
      showAssessmentDialog: false,
      showActivityChartDialog: false,
      selectedUserForChart: null,
      notification: {},
      searchbar: '',
    };
  },
  computed: {
    users() {
      return this.$store.getters.getAllActiveUsers;
    },
    getPopUpMessage() {
      return this.$t('UserAdministrationComponent.popup.deleteMessage', this.selectedUsers?.length);
    },
    getSchemas() {
      return this.$store.getters.getAvailableSchemas;
    },
    lang() {
      return this.$i18n.locale;
    },
    usersWithTimeData() {
      return this.users.map((user) => ({
        ...user,
        // Add activity status for sorting (active = 1, inactive = 0)
        activityStatus: this.getUserActiveStatus(user._id) === 'active' ? 1 : 0,
      }));
    },
    filteredUsers() {
      return this.usersWithTimeData
        .filter((e) => (e.name || '').toLowerCase().includes((this.searchbar || '').toLowerCase()));
    },
    currentUser() {
      return this.$store.getters.getUser?.userId;
    },
  },
  methods: {
    formatDate(originalDate) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'CET',
        hour12: false,
      };

      const getLocale = () => (
        (navigator.languages && navigator.languages.length)
          ? navigator.languages[0] : navigator.language);
      const Locale = getLocale();
      // Locale = 'en-US';
      // Locale = 'de-DE';

      const formattedDate = new Date(originalDate).toLocaleString(Locale, options);
      if (formattedDate === 'Invalid Date') {
        return ' ';
      }
      return formattedDate;
    },
    getProfilePicture(smallPic) {
      if (smallPic) {
        return smallPic;
      }
      return defaultProfileImage;
    },
    openPopup(user) {
      this.notification = {
        userData: user,
      };
      this.showDialog = true;
      /* this.$store.commit('addFrontendOverlay', {
        id: Math.random(),
        type: 'superUserProfile',
        userData: user,
        // teams: this.teams,
      }); */
    },
    async deleteCheckedUsers() {
      if (this.selectedUsers?.length > 0) {
        this.showPopup = true;
        this.$confirm.require({
          message: this.getPopUpMessage,
          header: 'Confirmation',
          icon: 'pi pi-exclamation-triangle',
          rejectProps: {
            label: this.cancelButtonText,
            severity: 'secondary',
            outlined: true,
          },
          acceptProps: {
            label: this.deleteButtonText,
          },
          accept: this.onPopupConfirm,
          reject: this.onPopupCancel,
        });
      }
    },
    onPopupCancel() {
      this.showPopup = false;
    },
    async onPopupConfirm() {
      this.showPopup = false;
      if (this.selectedUsers.some((user) => user._id === this.currentUser)) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: this.$t('UserAdministrationComponent.toast.deleteSelfError'),
        });
        return;
      }

      const data = {};
      data.userIDs = this.selectedUsers.map((item) => item._id);

      let successfulDelete = '';
      if (this.selectedUsers.length === 1) {
        successfulDelete = `${this.$t(
          'UserAdministrationComponent.toast.successfulDeleteForOne',
        )} ${this.selectedUsers[0].name}`;
      } else {
        successfulDelete = this.$t('UserAdministrationComponent.toast.successfulDelete');
      }

      let errorDelete = '';
      if (this.selectedUsers.length === 1) {
        errorDelete = `${this.$t('UserAdministrationComponent.toast.errorDeleteForOne')} ${
          this.selectedUsers[0].name
        }`;
      } else {
        errorDelete = this.$t('UserAdministrationComponent.toast.errorDelete');
      }

      try {
        this.showPopup = false;
        await this.$store.dispatch('deleteUser', data);
        await this.$store.dispatch('getAllFilteredUsers');
        this.$toast.add({
          severity: 'success',
          summary: successfulDelete,
          detail: '',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: errorDelete,
          detail: '',
          life: 3000,
        });
      } finally {
        this.selectedUsers = [];
      }
      // this.$store.dispatch('uncheckAllUsers');
    },
    toggle(event) {
      this.$refs.op.toggle(event);
    },
    toggleAssessment(event) {
      this.$refs.pop.toggle(event);
    },
    async downloadSelectedUsersCSV() {
      const downloadCSVData = {
        users: this.selectedUsers,
        lang: this.lang,
        allUsers: false,
      };
      this.loadingDownloadCSV = true;
      const { data } = await NetworkAdmin.postDownloadUserCSV(downloadCSVData);
      this.downloadCSVFromBlob(data);
    },
    async downloadAllUsersCSV() {
      const downloadCSVData = {
        users: {},
        lang: this.lang,
        allUsers: true,
      };

      this.loadingDownloadCSV = true;
      const { data } = await NetworkAdmin.postDownloadUserCSV(downloadCSVData);
      this.downloadCSVFromBlob(data);
    },
    downloadCSVFromBlob(data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'exportUsers.csv';
      a.style.display = 'none';
      document.body.appendChild(a);

      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      this.loadingDownloadCSV = false;
    },
    openAssessmentPopup(schemaName, schemaID) {
      this.$refs.pop.hide();
      if (this.selectedUsers.length !== 0) {
        this.notification = {
          userIDs: this.selectedUsers?.map((e) => e._id),
          schemaName,
          schemaID,
        };
        this.showAssessmentDialog = true;
      }
    },
    async changeUserTableLoadingState(bool) {
      this.loading = bool;
    },

    // ===== User Time KPI Methods =====

    /**
     * Get user's time on platform display.
     * Uses batch activity data (loaded on mount for all users) to ensure
     * consistent values across all table pages.
     * @param {string} userId - User ID
     * @returns {string} Formatted time display
     */
    getUserTimeOnPlatformDisplay(userId) {
      const activityData = this.$store.getters['userTimeKpi/getUserActivityData'](userId);

      if (!activityData) {
        return this.$t('UserAdministrationComponent.noTimeData');
      }

      const summary = getActivitySummary(activityData);
      if (!summary || summary.totalTime === 0) {
        return this.$t('UserAdministrationComponent.noTimeData');
      }

      return summary.formattedTotal;
    },

    /**
     * Get user's activity level based on actual user activity (last 7 days)
     * Uses: login events, assessments, level progress, devtips, travelbuddies
     * @param {string} userId - User ID
     * @returns {string} Activity level description
     */
    getUserActivityLevel(userId) {
      const activityData = this.$store.getters['userTimeKpi/getUserActivityData'](userId);

      if (!activityData) {
        return this.$t('UserAdministrationComponent.activityLevels.unknown');
      }

      // Count total activities within the time window (last 7 days)
      const loginEventsCount = activityData.loginEventsCount || 0;
      const submittedAssessmentsCount = activityData.submittedAssessmentsCount || 0;
      const levelProgressCount = activityData.levelProgressCount || 0;
      const devTipsCompletedCount = activityData.devTipsCompletedCount || 0;
      const travelBuddyCompletedCount = activityData.travelBuddyCompletedCount || 0;

      // Calculate total activity score
      const totalActivities = loginEventsCount
        + submittedAssessmentsCount
        + levelProgressCount
        + devTipsCompletedCount
        + travelBuddyCompletedCount;

      // Determine activity level based on total activities
      if (totalActivities >= 10) {
        return this.$t('UserAdministrationComponent.activityLevels.veryActive');
      }
      if (totalActivities >= 5) {
        return this.$t('UserAdministrationComponent.activityLevels.active');
      }
      if (totalActivities >= 2) {
        return this.$t('UserAdministrationComponent.activityLevels.moderate');
      }
      if (totalActivities >= 1) {
        return this.$t('UserAdministrationComponent.activityLevels.lowActivity');
      }
      return this.$t('UserAdministrationComponent.activityLevels.inactive');
    },

    /**
     * Calculate user active/inactive status based on actual user activity
     * Single source of truth: login events, assessments, level progress, devtips, travelbuddies
     * Session data is NOT used for active status (only for time on platform dialog)
     * @param {string} userId - User ID
     * @returns {string} 'active' or 'inactive'
     */
    getUserActiveStatus(userId) {
      const activityData = this.$store.getters['userTimeKpi/getUserActivityData'](userId);

      if (!activityData) {
        return 'inactive';
      }

      // Use the hasActivity flag from backend (within last 7 days)
      // This is determined by: login events, submitted assessments,
      // level progress, devtips completed, travelbuddy meetings
      if (activityData.hasActivity === true) {
        return 'active';
      }

      // Fallback: check individual activity counts
      const loginEventsCount = activityData.loginEventsCount || 0;
      const submittedAssessmentsCount = activityData.submittedAssessmentsCount || 0;
      const levelProgressCount = activityData.levelProgressCount || 0;
      const devTipsCompletedCount = activityData.devTipsCompletedCount || 0;
      const travelBuddyCompletedCount = activityData.travelBuddyCompletedCount || 0;

      // User is active if they have ANY activity within the time window (last 7 days)
      const hasAnyActivity = loginEventsCount > 0
        || submittedAssessmentsCount > 0
        || levelProgressCount > 0
        || devTipsCompletedCount > 0
        || travelBuddyCompletedCount > 0;

      return hasAnyActivity ? 'active' : 'inactive';
    },

    /**
     * Check if user activity is loading
     * @param {string} userId - User ID
     * @returns {boolean} Loading state
     */
    isLoadingUserActivity() {
      return this.$store.getters['userTimeKpi/isLoading']('activity');
    },

    /**
     * Show user activity chart dialog
     * @param {Object} user - User object
     */
    showUserActivityChart(user) {
      this.selectedUserForChart = user;
      this.showActivityChartDialog = true;
    },

    /**
     * Load user activity data for all users
     */
    async loadUserActivityData() {
      try {
        const userIds = this.users
          .map((user) => user._id)
          .filter((id) => id != null && id !== '');
        await this.$store.dispatch('userTimeKpi/fetchMultipleUsersLastWeekActivity', userIds);
      } catch (error) {
        console.error('Error loading user activity data:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load user activity data',
          life: 3000,
        });
      }
    },

    /**
     * Get user's raw time value for sorting (in milliseconds)
     * @param {string} userId - User ID
     * @returns {number} Raw time value in milliseconds
     */
    getUserRawTimeValue(userId) {
      const activityData = this.$store.getters['userTimeKpi/getUserActivityData'](userId);

      // Check if we have stored data (from calculate-and-get response)
      const metrics = activityData?.stored?.metrics || activityData?.metrics;

      if (!activityData || !metrics) {
        return 0; // Return 0 for users with no data so they sort to the bottom
      }

      return metrics.totalTimeSpent || 0;
    },
  },
  async mounted() {
    this.loading = true;
    await this.$store.dispatch('getAllFilteredUsers');
    await this.$store.dispatch('fetchFilterData');

    // Load assessment schemas for Create Assessment dropdown
    await this.$store.dispatch('assessmentSchemas/fetchAssessments');

    // Load user activity data after users are loaded
    await this.loadUserActivityData();

    this.loading = false;
  },
};
</script>
